import React, { useState } from 'react';
import {
  RefreshCw, ChevronDown, ChevronUp, AlertCircle, Loader2, X, RotateCcw,
} from 'lucide-react';
import { useAccessSync } from '../hooks/useAccessSync';
import type { FailedSyncEntry, SyncEntitySummary } from '@yawi/types';
import type { PartialSyncType } from '@yawi/domain';

// ============================================================
// Stat Card
// ============================================================
interface StatCardProps {
  label: string;
  synced: number;
  total: number;
  failed: number;
  syncType?: PartialSyncType;
  onSync?: (type: PartialSyncType) => void;
  syncing?: boolean;
  disabled?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  label, synced, total, failed, syncType, onSync, syncing, disabled,
}) => {
  const percentage = total > 0 ? Math.round((synced / total) * 100) : 0;
  const circumference = 2 * Math.PI * 20;
  const offset = circumference - (percentage / 100) * circumference;
  const isIncomplete = total > 0 && synced < total;

  return (
    <div className="bg-slate-50 rounded-xl p-3 flex flex-col items-center gap-1 min-w-[90px]">
      <svg width="52" height="52" viewBox="0 0 52 52">
        <circle cx="26" cy="26" r="20" fill="none" stroke="#e2e8f0" strokeWidth="5" />
        <circle
          cx="26"
          cy="26"
          r="20"
          fill="none"
          stroke={failed > 0 ? '#f97316' : '#22c55e'}
          strokeWidth="5"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 26 26)"
        />
        <text x="26" y="30" textAnchor="middle" fontSize="11" fontWeight="600" fill="#1e293b">
          {percentage}%
        </text>
      </svg>
      <p className="text-xs font-semibold text-slate-700">{label}</p>
      <p className="text-xs text-slate-500">
        {synced}/{total}
        {failed > 0 && <span className="text-orange-500 ml-1">({failed} err)</span>}
      </p>
      {syncType && onSync && isIncomplete && (
        <button
          type="button"
          onClick={() => onSync(syncType)}
          disabled={disabled || syncing}
          className="mt-0.5 text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 disabled:opacity-40"
          title={`Sync ${label}`}
        >
          {syncing ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
          Sync
        </button>
      )}
    </div>
  );
};

// ============================================================
// Progress Bar
// ============================================================
interface ProgressBarProps {
  completed: number;
  total: number;
  failed: number;
  percentage: number;
  onCancel: () => void;
  cancelling: boolean;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  completed, total, failed, percentage, onCancel, cancelling,
}) => (
  <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-blue-800">
        Syncing… {completed}/{total}
        {failed > 0 && <span className="text-orange-500 ml-1">({failed} failed)</span>}
      </span>
      <button
        type="button"
        onClick={onCancel}
        disabled={cancelling}
        className="text-xs text-slate-500 hover:text-red-600 flex items-center gap-1 disabled:opacity-50"
      >
        {cancelling ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
        Cancel
      </button>
    </div>
    <div className="w-full bg-blue-100 rounded-full h-2.5">
      <div
        className="bg-blue-500 h-2.5 rounded-full transition-all duration-500"
        style={{ width: `${percentage}%` }}
      />
    </div>
    <p className="text-xs text-blue-600 mt-1">{percentage}%</p>
  </div>
);

// ============================================================
// Failed Entry Row
// ============================================================
interface FailedEntryRowProps {
  entry: FailedSyncEntry;
  onRetry: (id: string) => void;
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  PERSON: 'Person',
  CARD: 'RFID Card',
  VEHICLE: 'Vehicle',
  GUEST_PERSON: 'Guest',
  DEVICE: 'Device',
};

const FailedEntryRow: React.FC<FailedEntryRowProps> = ({ entry, onRetry }) => (
  <div className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0">
    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 font-medium shrink-0">
      {ENTITY_TYPE_LABELS[entry.entityType] ?? entry.entityType}
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-slate-600 truncate font-mono">{entry.entityId.slice(0, 8)}…</p>
      {entry.lastError && (
        <p className="text-xs text-red-500 truncate">{entry.lastError}</p>
      )}
    </div>
    <span className="text-xs text-slate-400 shrink-0">
      ×{entry.retryCount}
    </span>
    <button
      type="button"
      onClick={() => onRetry(entry.id)}
      className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 shrink-0"
    >
      <RotateCcw size={11} />
      Retry
    </button>
  </div>
);

// ============================================================
// SyncDashboard
// ============================================================
export const SyncDashboard: React.FC = () => {
  const {
    status,
    failedEntries,
    failedTotal,
    loading,
    startingSync,
    startingPartialSync,
    retrying,
    cancelling,
    startSync,
    startPartialSync,
    retryAll,
    retrySingle,
    cancel,
  } = useAccessSync();

  const [showFailed, setShowFailed] = useState(false);

  if (loading) {
    return (
      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-sm text-slate-400">
        <Loader2 size={14} className="animate-spin" />
        Loading sync status…
      </div>
    );
  }

  if (!status) return null;

  const { summary, localTotals, isRunning, progress, lastFullSync } = status;

  const totalFailedCount =
    (summary.persons.failed ?? 0) +
    (summary.cards.failed ?? 0) +
    (summary.vehicles.failed ?? 0) +
    (summary.guests.failed ?? 0);

  const lastSyncLabel = lastFullSync
    ? `Last: ${new Date(lastFullSync.startedAt).toLocaleString()}`
    : 'Never synced';

  return (
    <div className="mt-5 pt-4 border-t border-slate-100 space-y-4">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-bold text-slate-700">ACS Sync Overview</h5>
        <span className="text-xs text-slate-400">{lastSyncLabel}</span>
      </div>

      {/* Stat cards */}
      <div className="flex gap-3 flex-wrap">
        <StatCard
          label="Persons"
          synced={summary.persons.synced}
          total={localTotals.activeMembers}
          failed={summary.persons.failed}
          syncType="members"
          onSync={!isRunning ? startPartialSync : undefined}
          syncing={startingPartialSync === 'members'}
          disabled={startingSync || startingPartialSync !== null}
        />
        <StatCard
          label="RFID Cards"
          synced={summary.cards.synced}
          total={localTotals.activeRfidCredentials}
          failed={summary.cards.failed}
        />
        <StatCard
          label="Vehicles"
          synced={summary.vehicles.synced}
          total={localTotals.activeVehicles}
          failed={summary.vehicles.failed}
          syncType="vehicles"
          onSync={!isRunning ? startPartialSync : undefined}
          syncing={startingPartialSync === 'vehicles'}
          disabled={startingSync || startingPartialSync !== null}
        />
        <StatCard
          label="Guests"
          synced={summary.guests.synced}
          total={localTotals.validGuests}
          failed={summary.guests.failed}
          syncType="guests"
          onSync={!isRunning ? startPartialSync : undefined}
          syncing={startingPartialSync === 'guests'}
          disabled={startingSync || startingPartialSync !== null}
        />
      </div>

      {/* Actions */}
      {!isRunning && (
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={startSync}
            disabled={startingSync}
            className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {startingSync ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} />
            )}
            Full Sync
          </button>

          {totalFailedCount > 0 && (
            <button
              type="button"
              onClick={() => retryAll()}
              disabled={retrying}
              className="px-3 py-1.5 border border-orange-200 text-orange-700 rounded-lg text-sm font-medium hover:bg-orange-50 disabled:opacity-50 flex items-center gap-2"
            >
              {retrying ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <RotateCcw size={14} />
              )}
              Retry Failed ({totalFailedCount})
            </button>
          )}
        </div>
      )}

      {/* Progress bar (during sync) */}
      {isRunning && progress && (
        <ProgressBar
          completed={progress.completed}
          total={progress.total}
          failed={progress.failed}
          percentage={progress.percentage}
          onCancel={cancel}
          cancelling={cancelling}
        />
      )}

      {/* Failed entries panel */}
      {failedTotal > 0 && (
        <div className="border border-orange-100 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setShowFailed(!showFailed)}
            className="w-full flex items-center justify-between px-4 py-2.5 bg-orange-50 hover:bg-orange-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <AlertCircle size={14} className="text-orange-500" />
              <span className="text-sm font-medium text-orange-700">
                Failed Entries ({failedTotal})
              </span>
            </div>
            {showFailed ? (
              <ChevronUp size={14} className="text-orange-500" />
            ) : (
              <ChevronDown size={14} className="text-orange-500" />
            )}
          </button>

          {showFailed && (
            <div className="bg-white px-4 py-2 max-h-60 overflow-y-auto">
              {failedEntries.length === 0 ? (
                <p className="text-xs text-slate-400 py-2">No failed entries loaded.</p>
              ) : (
                failedEntries.map((entry) => (
                  <FailedEntryRow key={entry.id} entry={entry} onRetry={retrySingle} />
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
